import {OctagonAlert} from "lucide-react";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";

import {Button} from "@/components/ui/button";
import {parseGpsCoordinatesString} from "@/src/modules/core/lib/format-gps-coordinates";
import {isViewingAsUser} from "@/src/modules/core/lib/impersonation";
import {resolveGpsForSubmit} from "@/src/modules/core/lib/resolve-gps-coordinates-string";
import {useToast} from "@/src/modules/core/hooks";
import {useStoredIsOnline} from "@/src/modules/core/store/is-online";
import {useStoredMe} from "@/src/modules/core/store/me";
import {useStoredResidentHouseholdId} from "@/src/modules/core/store/resident-context";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";

import {getPanicErrorCode, getPanicErrorTitle} from "../lib/panic-alert-error";
import {panicAlertsService} from "../services";
import {
  useClearPanicIntent,
  useStoredPanicIntentExpiresAt,
  useStoredPanicIntentToken,
} from "../store/panic-intent";
import {usePanicButtonConfig} from "../hooks/use-panic-button-config";
import {usePanicCancel, usePanicConfirm, usePanicIntent} from "../hooks/use-panic-intent";
import {PanicConfirmOverlay} from "./panic-confirm-overlay";

const HOLD_MS = 400;

export function PanicTriggerButton() {
  const {t} = useTranslation("panic");
  const toast = useToast();
  const me = useStoredMe();
  const isOnline = useStoredIsOnline();
  const userProperty = useStoredUserProperty();
  const residentHouseholdId = useStoredResidentHouseholdId();
  const clearIntent = useClearPanicIntent();
  const intentToken = useStoredPanicIntentToken();
  const intentExpiresAt = useStoredPanicIntentExpiresAt();
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [dialTargets, setDialTargets] = useState<Array<{label: string; phone_number: string}> | null>(null);
  const holdTimerRef = useRef<number | null>(null);
  const holdArmedRef = useRef(false);

  const panicEnabledByCompany = me?.company?.panic_button?.enabled === true;
  const canAttemptPanic =
    panicEnabledByCompany && panicAlertsService.getCanTrigger() && !isViewingAsUser(me);

  const {config, error: configError, isPending: isConfigPending} = usePanicButtonConfig(
    userProperty?.id,
    {enabled: canAttemptPanic},
  );

  const createIntent = usePanicIntent();
  const confirmIntent = usePanicConfirm();
  const cancelIntent = usePanicCancel();

  const configErrorCode = getPanicErrorCode(configError);
  const hideByConfigError =
    configErrorCode === "panic_button_disabled" || configErrorCode === "panic_button_role_denied";
  const canShow = canAttemptPanic && !isConfigPending && !hideByConfigError && config != null;

  useEffect(() => {
    if (!overlayOpen || !intentExpiresAt || !intentToken) {
      return;
    }
    const msLeft = new Date(intentExpiresAt).getTime() - Date.now();
    if (msLeft <= 0) {
      void cancelIntent.mutateAsync(intentToken).finally(() => {
        setOverlayOpen(false);
        clearIntent();
      });
      return;
    }
    const timeout = window.setTimeout(() => {
      void cancelIntent.mutateAsync(intentToken).finally(() => {
        setOverlayOpen(false);
        clearIntent();
      });
    }, msLeft + 100);
    return () => clearTimeout(timeout);
  }, [cancelIntent, clearIntent, intentExpiresAt, intentToken, overlayOpen]);

  if (!canShow) {
    return null;
  }

  const triggerIntent = async () => {
    if (!isOnline) {
      toast.error(t("toast.offline_cannot_queue"));
      return;
    }

    try {
      const gps = await resolveGpsForSubmit(
        {propertyCoordinates: null},
        {onFailure: "silent", captureContext: {feature: "panic", operation: "intent"}},
      );
      const coordinates = parseGpsCoordinatesString(gps?.gps_coordinates);
      const payload = {
        property_id: userProperty?.id,
        household_id: me?.isResident ? (residentHouseholdId ?? me.house_hold_id ?? undefined) : undefined,
        gps_coordinates: coordinates
          ? {latitude: coordinates.latitude, longitude: coordinates.longitude}
          : null,
      };
      await createIntent.mutateAsync(payload);
      setDialTargets(null);
      setOverlayOpen(true);
    } catch (error) {
      toast.error(getPanicErrorTitle(error));
    }
  };

  const onPointerDown = () => {
    if (holdTimerRef.current != null) {
      window.clearTimeout(holdTimerRef.current);
    }
    holdArmedRef.current = false;
    holdTimerRef.current = window.setTimeout(() => {
      holdArmedRef.current = true;
      holdTimerRef.current = null;
      void triggerIntent();
    }, HOLD_MS);
  };

  const clearHold = (showHint: boolean) => {
    if (holdTimerRef.current != null) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
      if (showHint && !holdArmedRef.current) {
        toast.info(t("toast.press_and_hold"));
      }
    }
  };

  const onCancelOverlay = async () => {
    if (intentToken) {
      try {
        await cancelIntent.mutateAsync(intentToken);
      } catch {
        // keep close flow resilient
      }
    }
    setOverlayOpen(false);
    setDialTargets(null);
    clearIntent();
  };

  const onConfirmOverlay = async () => {
    if (!intentToken) return;
    try {
      const gps = await resolveGpsForSubmit(
        {propertyCoordinates: null},
        {onFailure: "silent", captureContext: {feature: "panic", operation: "confirm"}},
      );
      const coordinates = parseGpsCoordinatesString(gps?.gps_coordinates);
      const response = await confirmIntent.mutateAsync({
        intent_token: intentToken,
        gps_coordinates: coordinates
          ? {latitude: coordinates.latitude, longitude: coordinates.longitude}
          : null,
      });
      setDialTargets(response.dial_targets);
      toast.success(t("toast.sent"));
      clearIntent();
    } catch (error) {
      toast.error(getPanicErrorTitle(error));
    }
  };

  return (
    <>
      <div className="pointer-events-none fixed right-4 bottom-4 z-50 md:right-6 md:bottom-6">
        <Button
          type="button"
          className="pointer-events-auto size-14 rounded-full shadow-lg"
          variant="destructive"
          onPointerDown={onPointerDown}
          onPointerUp={() => clearHold(true)}
          onPointerCancel={() => clearHold(false)}
          onPointerLeave={() => clearHold(false)}
          disabled={!isOnline || createIntent.isPending || confirmIntent.isPending}
        >
          <OctagonAlert className="size-6" />
        </Button>
      </div>
      <PanicConfirmOverlay
        open={overlayOpen}
        expiresAt={intentExpiresAt}
        onCancel={() => {
          void onCancelOverlay();
        }}
        onConfirm={() => {
          void onConfirmOverlay();
        }}
        isSubmitting={createIntent.isPending || confirmIntent.isPending || cancelIntent.isPending}
        dialTargets={dialTargets}
      />
    </>
  );
}
